import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useRouteError, useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import { LogProvider } from './context/LogContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import AIConsentModal from './components/AIConsentModal';
import { hasAIConsentDecision, saveAIConsent, saveAIConsentDeclined } from './components/AIConsentModal';


// Route-level error boundary for react-router v6
const RouteErrorBoundary = () => {
  const error = useRouteError();

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
          Something went wrong
        </h2>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginBottom: '24px', lineHeight: '1.5' }}>
          An unexpected error occurred. Please try again.
        </p>
        {/* DIAGNOSTIC: show error details to identify crash site */}
        <div style={{ background: 'rgba(255,0,0,0.1)', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'left' }}>
          <p style={{ color: '#ff6b6b', fontSize: '11px', fontWeight: '700', marginBottom: '4px' }}>🔍 DEBUG INFO:</p>
          <p style={{ color: '#ffaaaa', fontSize: '10px', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: '4px' }}>
            Type: {error?.constructor?.name || typeof error}
          </p>
          <p style={{ color: '#ffaaaa', fontSize: '10px', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: '4px' }}>
            Message: {error?.message || error?.status || JSON.stringify(error) || 'none'}
          </p>
          <p style={{ color: '#ffaaaa', fontSize: '10px', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: '4px' }}>
            Keys: {error ? JSON.stringify(Object.keys(error)) : 'null'}
          </p>
          <p style={{ color: '#ffaaaa', fontSize: '9px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            Stack: {(error?.stack || error?.componentStack || '').slice(0, 300)}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={() => { window.location.href = '/dashboard'; }}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff', border: 'none', borderRadius: '8px',
              padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff', border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px', padding: '12px 24px', fontSize: '14px', cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
};

// Lazy-loaded views for code splitting
const AuthLanding = lazy(() => import('./views/AuthLanding'));
const SignIn = lazy(() => import('./views/SignIn'));
const Onboarding = lazy(() => import('./views/Onboarding'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const Scan = lazy(() => import('./views/Scan'));
const Exercise = lazy(() => import('./views/Exercise'));
const Meals = lazy(() => import('./views/Meals'));
const Profile = lazy(() => import('./views/Profile'));
const Settings = lazy(() => import('./views/Settings'));
const Referrals = lazy(() => import('./views/Referrals'));
const Schedule = lazy(() => import('./views/Schedule'));
const Favorites = lazy(() => import('./views/Favorites'));
const EarningsDashboard = lazy(() => import('./views/EarningsDashboard'));
const FinancialDashboard = lazy(() => import('./views/FinancialDashboard'));
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./views/TermsOfService'));
const ContactUs = lazy(() => import('./views/ContactUs'));
const FAQ = lazy(() => import('./views/FAQ'));
const HealthDisclaimer = lazy(() => import('./views/HealthDisclaimer'));
const AdminUsers = lazy(() => import('./views/AdminUsers'));
const SuperAdminDashboard = lazy(() => import('./views/SuperAdminDashboard'));
const SubscriptionSuccess = lazy(() => import('./views/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('./views/SubscriptionCancel'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(255,255,255,0.1)',
      borderTop: '3px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Suspense wrapper for lazy components
const LazyComponent = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);


// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { userProfile, isInitialized, isLoadingProfile } = useUser();

  // Wait for profile to finish loading before deciding
  if (!isInitialized || isLoadingProfile) return null;

  // Check if user has completed onboarding:
  // - If userProfile has a goal set (from Supabase), they've completed onboarding
  // - If localStorage has the onboarding complete flag (sync fallback for fast navigation)
  const hasCompletedOnboarding = !!userProfile.goal || localStorage.getItem('calobite_onboarding_complete') === 'true';

  // If no goal set, redirect to onboarding
  if (!hasCompletedOnboarding && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        // Redirect to auth landing page first
        element: <Navigate to="/auth" replace />
      },
      {
        path: 'auth',
        element: <LazyComponent><AuthLanding /></LazyComponent>
      },
      {
        path: 'signin',
        element: <LazyComponent><SignIn /></LazyComponent>
      },
      {
        path: 'onboarding',
        element: <LazyComponent><Onboarding /></LazyComponent>
      },
      {
        path: 'dashboard',
        element: <ProtectedRoute><LazyComponent><Dashboard /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'scan',
        element: <ProtectedRoute><LazyComponent><Scan /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'exercise',
        element: <ProtectedRoute><LazyComponent><Exercise /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'meals',
        element: <ProtectedRoute><LazyComponent><Meals /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'profile',
        element: <ProtectedRoute><LazyComponent><Profile /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'settings',
        element: <ProtectedRoute><LazyComponent><Settings /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'referrals',
        element: <ProtectedRoute><LazyComponent><Referrals /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'schedule',
        element: <ProtectedRoute><LazyComponent><Schedule /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'favorites',
        element: <ProtectedRoute><LazyComponent><Favorites /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'earnings',
        element: <ProtectedRoute><LazyComponent><EarningsDashboard /></LazyComponent></ProtectedRoute>
      },
      {
        path: 'financial',
        element: <ProtectedRoute><LazyComponent><FinancialDashboard /></LazyComponent></ProtectedRoute>
      },
      // Hidden Admin Dashboard - protected by email check in component
      {
        path: 'admin',
        element: <LazyComponent><SuperAdminDashboard /></LazyComponent>
      },
      {
        path: 'admin/users',
        element: <LazyComponent><AdminUsers /></LazyComponent>
      },
      // Subscription flow pages (no layout protection needed)
      {
        path: 'subscription/success',
        element: <LazyComponent><SubscriptionSuccess /></LazyComponent>
      },
      {
        path: 'subscription/cancel',
        element: <LazyComponent><SubscriptionCancel /></LazyComponent>
      }
    ]
  },
  // Public info pages (outside of Layout to avoid navigation/paywall popups during onboarding)
  {
    path: '/privacy',
    element: <LazyComponent><PrivacyPolicy /></LazyComponent>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: '/terms',
    element: <LazyComponent><TermsOfService /></LazyComponent>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: '/health-disclaimer',
    element: <LazyComponent><HealthDisclaimer /></LazyComponent>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: '/contact',
    element: <LazyComponent><ContactUs /></LazyComponent>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: '/faq',
    element: <LazyComponent><FAQ /></LazyComponent>,
    errorElement: <RouteErrorBoundary />
  }
]);

const App = () => {
  // ── AI Consent cold-launch re-prompt ─────────────────────────────────────
  // If the user has completed onboarding but no consent decision exists for
  // the current disclosure version (e.g. pre-existing install, new version),
  // show the modal so Apple reviewers (and returning users) always encounter it.
  const [showConsentModal, setShowConsentModal] = useState(false);

  React.useEffect(() => {
    console.log('📱 [IOS_DEBUG] App Component Mounted');
    console.log('📱 [IOS_DEBUG] Environment:', import.meta.env.MODE);

    // Initialize RevenueCat IAP SDK
    import('./services/iapService').then(({ initIAP, addEntitlementListener }) => {
      initIAP().catch(err => console.error('[IAP] Init error:', err));

      // ── Wire up RevenueCat real-time entitlement listener ─────────────────
      // This fires whenever the OS confirms a purchase, restore, or expiry change.
      // It's the canonical fix for Guideline 2.1(b): the subscriber tier updates
      // immediately after payment without requiring an app restart.
      // We dispatch a custom event so SubscriptionContext can react without
      // a React tree dependency here in App.jsx (which sits outside the context).
      const unsubscribeEntitlement = addEntitlementListener((tier, customerInfo) => {
        console.log('[App] ⚡ RevenueCat entitlement change detected → tier:', tier);
        window.dispatchEvent(new CustomEvent('calobite_entitlement_changed', {
          detail: { tier, customerInfo }
        }));
      });

      // Store cleanup for unmount
      window.__calobiteEntitlementUnsub = unsubscribeEntitlement;
    }).catch(err => console.error('[IAP] Failed to load iapService:', err));

    // ── AI Consent cold-launch re-prompt ─────────────────────────────────
    // Fire quickly so user sees it before navigating to an AI feature.
    const consentTimer = setTimeout(() => {
      const onboardingComplete = localStorage.getItem('calobite_onboarding_complete') === 'true';
      if (onboardingComplete && !hasAIConsentDecision()) {
        console.log('[App] No AI consent decision found — showing re-prompt for existing install');
        setShowConsentModal(true);
      }
    }, 100);

    // ⛔ STARTUP SESSION GUARDRAIL:
    const currentEmail = localStorage.getItem('calobite_current_user');
    if (currentEmail) {
      import('./utils/nuclearReset').then(({ validateSessionFromDB }) => {
        validateSessionFromDB(currentEmail).then(result => {
          if (!result) {
            console.warn('[App] Startup validation: no DB profile found for cached email. Clearing session.');
            import('./utils/nuclearReset').then(({ nuclearWipeSession }) => {
              nuclearWipeSession(currentEmail);
            });
          } else {
            console.log('[App] ✅ Startup session validated from DB:', result.subscriptionTier, '| isSuperAdmin:', result.isSuperAdmin);
          }
        });
      });
    }

    return () => clearTimeout(consentTimer);
  }, []);

  return (
    <AuthProvider>
      <UserProvider>
        <SubscriptionProvider>
          <LogProvider>
            {/* AI Consent cold-launch re-prompt overlay (Apple Guideline 5.1.2) */}
            {showConsentModal && (
              <AIConsentModal
                isOpen={showConsentModal}
                onAccept={() => {
                  saveAIConsent();
                  setShowConsentModal(false);
                }}
                onDecline={() => {
                  saveAIConsentDeclined();
                  setShowConsentModal(false);
                }}
              />
            )}
            <RouterProvider router={router} />
          </LogProvider>
        </SubscriptionProvider>
      </UserProvider>
    </AuthProvider>
  );
};

export default App;
